import React from 'react';
import { Badge, Button, Card, ConfirmDialog, Spinner, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';

function assignmentLabel(a, rolesById, propsById, t) {
  const role = rolesById.get(a.roleId)?.name ?? t('Unknown role');
  const scope = a.propertyId ? (propsById.get(a.propertyId)?.name ?? t('Property')) : t('Org-wide');
  return `${role} · ${scope}`;
}

export default function UsersTab({ users, loading, rolesById, propsById, canManage, currentUserId, onEdit, onChanged }) {
  const toast = useToast();
  const t = useT();
  const [busyId, setBusyId] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      if (u.isActive) await usersApi.deactivateUser(u.id);
      else await usersApi.activateUser(u.id);
      toast.success(u.isActive ? t('User deactivated') : t('User activated'));
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not update user'), { description: err?.message });
    } finally {
      setBusyId(null);
    }
  };

  const sendReset = async (u) => {
    setBusyId(u.id);
    try {
      await usersApi.sendPasswordReset(u.id);
      toast.success(t('Password reset email sent to {email}', { email: u.email }));
    } catch (err) {
      toast.error(t('Could not send reset email'), { description: err?.message });
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await usersApi.deleteUser(confirmDelete.id);
      toast.success(t('User deleted'));
      setConfirmDelete(null);
      await onChanged?.();
    } catch (err) {
      toast.error(t('Could not delete user'), { description: err?.message });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-6)' }}><Spinner size={24} /></div>;
  }

  return (
    <div className="set-prop-list">
      {users.map((u) => {
        const isSelf = u.id === currentUserId;
        const busy = busyId === u.id;
        return (
          <Card key={u.id} variant="flat">
            <Card.Body>
              <div className="set-prop">
                <div className="set-prop__main">
                  <span className="set-prop__name">
                    {u.firstName} {u.lastName}
                    {isSelf && <Badge tone="neutral">{t('You')}</Badge>}
                    {!u.isActive && <Badge tone="warning">{t('Inactive')}</Badge>}
                  </span>
                  <span className="usr-email">{u.email}</span>
                  <span className="usr-chips">
                    {u.roleAssignments.length === 0 && <Badge tone="neutral">{t('No roles')}</Badge>}
                    {u.roleAssignments.map((a, i) => (
                      <Badge key={i} tone="brand">{assignmentLabel(a, rolesById, propsById, t)}</Badge>
                    ))}
                  </span>
                </div>
                {canManage && (
                  <div className="usr-actions">
                    <Button variant="ghost" size="sm" leadingIcon={<Icon.Pencil size={15} />} onClick={() => onEdit(u)}>
                      {t('Edit')}
                    </Button>
                    {u.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={busy}
                        leadingIcon={<Icon.KeyRound size={15} />}
                        onClick={() => sendReset(u)}
                      >
                        {t('Send reset')}
                      </Button>
                    )}
                    {!isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={busy}
                        leadingIcon={u.isActive ? <Icon.Ban size={15} /> : <Icon.Check size={15} />}
                        onClick={() => toggleActive(u)}
                      >
                        {u.isActive ? t('Deactivate') : t('Activate')}
                      </Button>
                    )}
                    {!isSelf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={<Icon.Trash size={15} />}
                        onClick={() => setConfirmDelete(u)}
                      >
                        {t('Delete')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        );
      })}

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('Delete user')}
        description={confirmDelete
          ? t('Delete {name} ({email})? This permanently removes their account and sign-in access.', {
              name: `${confirmDelete.firstName} ${confirmDelete.lastName}`,
              email: confirmDelete.email,
            })
          : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={!!confirmDelete && busyId === confirmDelete.id}
        onConfirm={doDelete}
        onCancel={() => (busyId ? null : setConfirmDelete(null))}
      />
    </div>
  );
}
